// Static charter quote-request form — the deterministic fallback the webchat offers when the chat
// misreads a rider or errors. Mirrors Peoria Charter's real "Quote Request" form (Group Information
// + Charter Information), in Movorra design language (semantic tokens + shared primitives). Fields
// are keyed by charter slot id so the payload maps 1:1 onto the planner's slot schema server-side.
//
// This component only collects + validates. Submission (session id, transcript, API call) is owned
// by CustomerApp via the `onSubmit(values)` prop, which resolves to { ok, error }.

import React, { useState } from 'react';
import { Field, TextInput, Select, Button } from '../ui/primitives';
import { US_STATES, isValidPhone, isValidEmail, isPastDateTime, nowLocalISO, returnBeforeOutbound } from './validation';

// Peoria's real trip-type options. `value` is the charter `trip_type` slot value; options with no
// slot equivalent (Special, Private Ride) carry `raw` — recorded in comments so nothing is lost.
const TRIP_TYPES = [
  { label: 'One Way', value: 'one_way' },
  { label: 'Round Trip', value: 'round_trip' },
  { label: 'Multi-Leg', value: 'multi_leg' },
  { label: 'Recurring Shuttle', value: 'shuttle' },
  { label: 'Unique / Custom', value: 'special_request' },
  { label: 'Special', value: '', raw: 'Special' },
  { label: 'Private Ride', value: '', raw: 'Private Ride' },
];
const ROUND_TRIP_LABELS = new Set(['Round Trip']);
const SHUTTLE_LABELS = new Set(['Recurring Shuttle']);

// Shared input classes — mirrors the primitives' INPUT_BASE (not exported) so the datetime pickers
// and the comments textarea match TextInput / Select.
const FIELD_INPUT =
  'w-full rounded-btn border border-peoria-border bg-peoria-surface2 text-sm text-peoria-text placeholder:text-peoria-muted ' +
  'px-3 py-2 transition-colors duration-150 hover:border-peoria-muted/40 focus:outline-none ' +
  'focus:border-peoria-red focus:ring-2 focus:ring-peoria-red/30';

// datetime-local yields "2026-07-11T15:00"; normalize to "2026-07-11 15:00" for a clean, unambiguous
// value the dispatcher reads on the lead.
const normalizeDateTime = (v) => (v ? String(v).replace('T', ' ') : v);

// Slot-id fields that must be filled. return_datetime is required conditionally (round trips only).
const REQUIRED = [
  'group_name', 'contact_name', 'phone', 'email', 'address', 'city', 'state', 'zip',
  'departure_city', 'departure_state', 'departure_datetime',
  'destination_city', 'destination_state', 'passenger_count', 'comments',
];

const EMPTY = {
  group_name: '', contact_name: '', phone: '', phone_ext: '', email: '', billing_email: '',
  address: '', city: '', state: '', zip: '',
  departure_city: '', departure_state: '', departure_datetime: '',
  destination_city: '', destination_state: '', return_datetime: '',
  passenger_count: '', requested_driver: '', comments: '',
};

function Req() {
  return <span className="text-danger"> *</span>;
}

// Section heading matching the muted eyebrow style used across the charter surfaces.
function SectionTitle({ children }) {
  return (
    <h3 className="text-13 font-semibold uppercase tracking-brand text-peoria-red pt-2">{children}</h3>
  );
}

export default function CharterQuoteForm({ initialValues, onSubmit, onCancel }) {
  const [form, setForm] = useState(() => ({ ...EMPTY, ...(initialValues || {}) }));
  const [tripTypeLabel, setTripTypeLabel] = useState('');
  const [shuttleSchedule, setShuttleSchedule] = useState('');
  const [alcohol, setAlcohol] = useState(false);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));
  const isRoundTrip = ROUND_TRIP_LABELS.has(tripTypeLabel);
  const isShuttle = SHUTTLE_LABELS.has(tripTypeLabel);

  const hint = (key, base) =>
    errors[key] ? <span className="text-danger">{errors[key]}</span> : base;

  function validate() {
    const next = {};
    for (const key of REQUIRED) {
      if (!String(form[key] || '').trim()) next[key] = 'Required';
    }
    // Per-field FORMAT validation (each field separately), only when present — the "Required" check above
    // already covers empties. Matches the Python agent's per-field contact validation.
    if (String(form.phone || '').trim() && !isValidPhone(form.phone)) {
      next.phone = 'Enter a 10-digit phone number with the area code';
    }
    if (String(form.email || '').trim() && !isValidEmail(form.email)) {
      next.email = "That email doesn't look right";
    }
    // Not-in-past: the native `min` on the pickers is bypassable (typed input), so re-check here.
    if (String(form.departure_datetime || '').trim() && isPastDateTime(form.departure_datetime)) {
      next.departure_datetime = 'Pick a date and time in the future';
    }
    if (String(form.return_datetime || '').trim() && isPastDateTime(form.return_datetime)) {
      next.return_datetime = 'Pick a date and time in the future';
    }
    if (!tripTypeLabel) next.trip_type = 'Required';
    if (isRoundTrip && !String(form.return_datetime || '').trim()) {
      next.return_datetime = 'Required for a round trip';
    }
    // A round trip's return must not be before departure.
    if (isRoundTrip && String(form.return_datetime || '').trim()
        && String(form.departure_datetime || '').trim()
        && returnBeforeOutbound(form.departure_datetime, form.return_datetime)) {
      next.return_datetime = 'The return must be on or after the departure';
    }
    return next;
  }

  async function handleSubmit(e) {
    e.preventDefault();
    if (submitting) return;
    const found = validate();
    setErrors(found);
    if (Object.keys(found).length) return;

    // Build the slot-keyed payload. Trip type maps to the `trip_type` slot; Special / Private Ride
    // have no slot value, so their label is appended to comments instead of being lost.
    const chosen = TRIP_TYPES.find((t) => t.label === tripTypeLabel) || {};
    let comments = String(form.comments || '').trim();
    if (!chosen.value && chosen.raw) {
      comments = comments ? `${comments}\n[Trip type: ${chosen.raw}]` : `[Trip type: ${chosen.raw}]`;
    }
    // Shuttle schedule stays free-text (no structured slots on the static form) — append it to
    // comments so the dispatcher / planner sees it alongside everything else.
    const schedule = isShuttle ? String(shuttleSchedule || '').trim() : '';
    if (schedule) {
      comments = comments ? `${comments}\n[Shuttle schedule: ${schedule}]` : `[Shuttle schedule: ${schedule}]`;
    }

    const values = {};
    for (const [k, v] of Object.entries(form)) {
      const val = String(v || '').trim();
      if (val) values[k] = val;
    }
    // Picker values come through as ISO ("…T…"); store the dispatcher-readable form.
    if (values.departure_datetime) values.departure_datetime = normalizeDateTime(values.departure_datetime);
    if (values.return_datetime) values.return_datetime = normalizeDateTime(values.return_datetime);
    values.comments = comments;
    if (chosen.value) values.trip_type = chosen.value;
    else delete values.trip_type;
    if (alcohol) values.alcohol_agreement = 'yes';

    setSubmitting(true);
    setSubmitError('');
    try {
      const res = await onSubmit(values);
      // On success the parent closes the form (this unmounts); on failure re-enable so they can retry.
      if (!res || !res.ok) {
        setSubmitting(false);
        setSubmitError(res && res.error === 'ALREADY_BOOKED'
          ? 'This booking has already been confirmed by our team — no need to resubmit.'
          : "We couldn't send that just now. Please try again in a moment.");
      }
    } catch (_) {
      setSubmitting(false);
      setSubmitError("We couldn't send that just now. Please try again in a moment.");
    }
  }

  return (
    <div className="flex-1 min-h-0 overflow-y-auto bg-peoria-bg">
      <form onSubmit={handleSubmit} className="mx-auto max-w-2xl px-5 py-6 space-y-5" noValidate>
        <div>
          <h2 className="text-lg font-semibold text-peoria-text">Quote request</h2>
          <p className="text-13 text-peoria-muted mt-1">
            Fill this in and our team will follow up with a quote. Anything you've already told us in
            the chat comes along automatically.
          </p>
        </div>

        <SectionTitle>Group information</SectionTitle>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field tone="peoria" label={<>Group name<Req /></>} hint={hint('group_name')}>
            <TextInput tone="peoria" value={form.group_name} onChange={set('group_name')} placeholder="Group name" />
          </Field>
          <Field tone="peoria" label={<>Contact name<Req /></>} hint={hint('contact_name')}>
            <TextInput tone="peoria" value={form.contact_name} onChange={set('contact_name')} placeholder="Contact name" />
          </Field>
          <Field tone="peoria" label={<>Telephone<Req /></>} hint={hint('phone')}>
            <TextInput tone="peoria" value={form.phone} onChange={set('phone')} placeholder="Telephone" type="tel"
              inputMode="tel" />
          </Field>
          <Field tone="peoria" label="Extension">
            <TextInput tone="peoria" value={form.phone_ext} onChange={set('phone_ext')} placeholder="Ext." />
          </Field>
          <Field tone="peoria" label={<>Email<Req /></>} hint={hint('email')}>
            <TextInput tone="peoria" value={form.email} onChange={set('email')} placeholder="Email" type="email" />
          </Field>
          <Field tone="peoria" label="Billing email">
            <TextInput tone="peoria" value={form.billing_email} onChange={set('billing_email')}
              placeholder="Billing email" type="email" />
          </Field>
          <div className="sm:col-span-2">
            <Field tone="peoria" label={<>Address<Req /></>} hint={hint('address')}>
              <TextInput tone="peoria" value={form.address} onChange={set('address')} placeholder="Address" />
            </Field>
          </div>
          <Field tone="peoria" label={<>City<Req /></>} hint={hint('city')}>
            <TextInput tone="peoria" value={form.city} onChange={set('city')} placeholder="City" />
          </Field>
          <Field tone="peoria" label={<>State<Req /></>} hint={hint('state')}>
            <Select tone="peoria" value={form.state} onChange={set('state')}>
              <option value="">State</option>
              {US_STATES.map((s) => <option key={s} value={s}>{s}</option>)}
            </Select>
          </Field>
          <Field tone="peoria" label={<>Zip code<Req /></>} hint={hint('zip')}>
            <TextInput tone="peoria" value={form.zip} onChange={set('zip')} placeholder="Zip code" inputMode="numeric" />
          </Field>
        </div>

        <SectionTitle>Charter information</SectionTitle>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field tone="peoria" label={<>Departure city<Req /></>} hint={hint('departure_city')}>
            <TextInput tone="peoria" value={form.departure_city} onChange={set('departure_city')} placeholder="Departure city" />
          </Field>
          <Field tone="peoria" label={<>Departure state<Req /></>} hint={hint('departure_state')}>
            <Select tone="peoria" value={form.departure_state} onChange={set('departure_state')}>
              <option value="">State</option>
              {US_STATES.map((s) => <option key={s} value={s}>{s}</option>)}
            </Select>
          </Field>
          <div className="sm:col-span-2">
            <Field tone="peoria" label={<>Departure date &amp; time<Req /></>} hint={hint('departure_datetime')}>
              <input type="datetime-local" value={form.departure_datetime} min={nowLocalISO()}
                onChange={set('departure_datetime')} className={FIELD_INPUT} />
            </Field>
          </div>
          <Field tone="peoria" label={<>Destination city<Req /></>} hint={hint('destination_city')}>
            <TextInput tone="peoria" value={form.destination_city} onChange={set('destination_city')} placeholder="Destination city" />
          </Field>
          <Field tone="peoria" label={<>Destination state<Req /></>} hint={hint('destination_state')}>
            <Select tone="peoria" value={form.destination_state} onChange={set('destination_state')}>
              <option value="">State</option>
              {US_STATES.map((s) => <option key={s} value={s}>{s}</option>)}
            </Select>
          </Field>
          <div className="sm:col-span-2">
            <Field tone="peoria" label={<>Return date &amp; time{isRoundTrip && <Req />}</>}
              hint={isRoundTrip ? hint('return_datetime') : hint('return_datetime', 'Only for round trips.')}>
              <input type="datetime-local" value={form.return_datetime} min={form.departure_datetime || nowLocalISO()}
                onChange={set('return_datetime')} className={FIELD_INPUT} />
            </Field>
          </div>
          <Field tone="peoria" label={<>Trip type<Req /></>} hint={hint('trip_type')}>
            <Select tone="peoria" value={tripTypeLabel} onChange={(e) => setTripTypeLabel(e.target.value)}>
              <option value="">Trip type</option>
              {TRIP_TYPES.map((t) => <option key={t.label} value={t.label}>{t.label}</option>)}
            </Select>
          </Field>
          <Field tone="peoria" label={<>Passenger count<Req /></>} hint={hint('passenger_count')}>
            <TextInput tone="peoria" value={form.passenger_count} onChange={set('passenger_count')}
              placeholder="Passenger count" inputMode="numeric" />
          </Field>
          {isShuttle && (
            <div className="sm:col-span-2">
              <Field tone="peoria" label="Shuttle schedule (dates, days of week, service window)">
                <textarea
                  value={shuttleSchedule}
                  onChange={(e) => setShuttleSchedule(e.target.value)}
                  placeholder="e.g. June 2–13, Mon–Fri, first pickup 7:00 AM, last return 6:30 PM"
                  rows={3}
                  className={`${FIELD_INPUT} resize-y`}
                />
              </Field>
            </div>
          )}
          <div className="sm:col-span-2">
            <Field tone="peoria" label="Requested driver">
              <TextInput tone="peoria" value={form.requested_driver} onChange={set('requested_driver')}
                placeholder="Requested driver" />
            </Field>
          </div>
          <div className="sm:col-span-2">
            <Field tone="peoria" label={<>Comments<Req /></>} hint={hint('comments')}>
              <textarea
                value={form.comments}
                onChange={set('comments')}
                placeholder="Comments"
                rows={4}
                className={`${FIELD_INPUT} resize-y`}
              />
            </Field>
          </div>
          <div className="sm:col-span-2">
            <label className="flex items-center gap-2.5 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={alcohol}
                onChange={(e) => setAlcohol(e.target.checked)}
                className="h-4 w-4 rounded border-peoria-border text-peoria-red focus:ring-peoria-red/30 cursor-pointer"
              />
              <span className="text-sm text-peoria-text">Include alcohol contract</span>
            </label>
          </div>
        </div>

        {submitError && (
          <div className="rounded-btn border border-danger/30 bg-danger/10 px-3 py-2 text-13 text-danger">
            {submitError}
          </div>
        )}

        <div className="flex flex-col-reverse sm:flex-row gap-3 pt-2">
          <Button type="button" variant="peoriaOutline" size="lg" onClick={onCancel} className="sm:flex-1"
            disabled={submitting}>
            Back to chat
          </Button>
          <Button type="submit" variant="peoria" size="lg" className="sm:flex-1" disabled={submitting}>
            {submitting ? 'Submitting…' : 'Submit request'}
          </Button>
        </div>
      </form>
    </div>
  );
}
